import { Plugin } from '@tiptap/pm/state';

export interface ValidPositions {
  beforeMatch?: string[];
  afterMatch?: string[];
}

export interface TransactionFilterOptions {
  nodeName: string;
  validPositions?: ValidPositions;
}

/**
 * Creates a ProseMirror plugin that filters transactions to prevent invalid custom section insertions
 * - Prevents nesting of custom sections within other custom sections
 * - Validates neighbor nodes based on validPositions configuration
 * - Allows insertion before other custom sections of the same type
 */
export const createCustomSectionTransactionFilter = (
  options: TransactionFilterOptions,
): Plugin => {
  return new Plugin({
    filterTransaction: (tr, state) => {
      const { doc } = tr;
      const customSectionType = state.schema.nodes[options.nodeName];

      // Check if a new custom section is inserted
      let blocked = false;

      tr.steps.forEach((step: any) => {
        const slice = step.slice;

        if (!slice) {
          return;
        }

        slice.content.forEach((node: any) => {
          if (node.type === customSectionType) {
            const $pos = doc.resolve(step.from);

            // Prevent nesting
            for (let d = $pos.depth; d > 0; d--) {
              if ($pos.node(d).type === customSectionType) {
                blocked = true;

                return;
              }
            }

            // Validate neighbors - allow insertion before custom sections
            const before = $pos.nodeBefore?.type.name;
            const after = $pos.nodeAfter?.type.name;

            // Allow insertion before custom sections (this was the original intent)
            if (after === options.nodeName) {
              return; // Allow this insertion
            }

            // For other positions, validate against validPositions
            const validBefore =
              !before || options.validPositions?.beforeMatch?.includes(before);
            const validAfter =
              !after || options.validPositions?.afterMatch?.includes(after);

            if (!validBefore || !validAfter) {
              blocked = true;
            }
          }
        });
      });

      return !blocked;
    },
  });
};
